import * as THREE from 'three';

/**
 * Edge-of-screen arrows toward cop units (driver perspective). Uses camera basis
 * so arrows stay stable while the chase cam orbits.
 */
export class ThreatIndicator {
  private readonly root: HTMLElement | null;
  private readonly arrows: HTMLElement[] = [];

  constructor() {
    this.root = document.getElementById('threat-overlay');
    if (!this.root) return;
    for (let i = 0; i < 8; i++) {
      const a = document.createElement('div');
      a.className = 'threat-arrow';
      a.setAttribute('aria-hidden', 'true');
      this.root.appendChild(a);
      this.arrows.push(a);
    }
  }

  update(
    camera: THREE.PerspectiveCamera,
    width: number,
    height: number,
    playerX: number,
    playerZ: number,
    cops: { x: number; z: number }[],
    active: boolean,
    distNearest: number,
    closingFast: boolean,
  ): void {
    if (!this.root) return;
    if (!active || cops.length === 0) {
      this.root.dataset.visible = 'false';
      return;
    }

    const cx = width * 0.5;
    const cy = height * 0.5;
    const margin = 38;
    const rx = cx - margin;
    const ry = cy - margin;

    const camDir = new THREE.Vector3();
    camera.getWorldDirection(camDir);
    const camRight = new THREE.Vector3()
      .crossVectors(camDir, camera.up)
      .normalize();
    const camUp = camera.up.clone().normalize();

    for (let i = 0; i < this.arrows.length; i++) {
      const el = this.arrows[i];
      const cop = cops[i];
      if (!cop) {
        el.style.opacity = '0';
        continue;
      }

      const to = new THREE.Vector3(
        cop.x - playerX,
        0,
        cop.z - playerZ,
      );
      const dist = to.length();
      if (dist < 2) {
        el.style.opacity = '0';
        continue;
      }
      to.multiplyScalar(1 / dist);

      let sx = to.dot(camRight);
      let sy = to.dot(camUp);
      if (to.dot(camDir) < 0) {
        sx = -sx;
        sy = -sy;
      }
      const sl = Math.hypot(sx, sy);
      if (sl < 1e-4) {
        el.style.opacity = '0';
        continue;
      }
      sx /= sl;
      sy /= sl;

      const angle = Math.atan2(sx, sy);
      const ex = cx + Math.sin(angle) * rx;
      const ey = cy - Math.cos(angle) * ry;

      const near = distNearest < 200;
      const pulse = near && closingFast ? 1 : near ? 0.88 : 0.62;
      el.style.opacity = String(pulse);
      el.style.left = `${ex}px`;
      el.style.top = `${ey}px`;
      el.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;
      el.dataset.near = near ? 'true' : 'false';
    }

    this.root.dataset.visible = 'true';
  }
}
